import { get, merge } from "lodash";
import {
  CITY,
  CODE,
  COMPANY_NAME,
  COUNTRY,
  CUSTOMER_PURCHASE_ORDER_NUMBER,
  DISCREET,
  LINE1,
  LINE2,
  LINE_ITEMS,
  POSTAL_CODE,
  SHIP_TO_TYPE,
  STATE,
  TYPE
} from "APP_ROOT/constants";
import {
  COUNTRY_OF_MFG,
  DESCRIPTION,
  ECCN,
  HTSUS_CODE,
  PART_NUMBER,
  SCHEDULE_B_OR_HT_CODE,
  WEIGHT
} from "MODULES/parts/constants";
import removeKey from "LIB/removeKey";
import { REPAIR_AND_RETURN } from "MODULES/Shipping/constants";
import { useCurrentUser } from "APP_ROOT/hooks";
import { RO_ITEM } from "MODULES/Orders/shared/constants";
import { CUSTOMER } from "MODULES/shared/components/ShippingSection/shippingTypes";
import pojo from "../../../../lib/pojos";

export const getEin = (salesOrder, repairOrder) => {
  if (salesOrder && salesOrder.line_items) {
    const locationId = get(
      salesOrder,
      "line_items[0].inventory.store_location_id",
      ""
    );

    const location = useCurrentUser().locations.filter(
      l => locationId === l.id
    )[0];
    if (location) {
      return location.tax_id_number;
    }
  } else if (repairOrder && repairOrder.line_items) {
    const locationId = get(
      repairOrder,
      "line_items[0].inventory.store_location_id",
      ""
    );
    const location = useCurrentUser().locations.filter(
      l => locationId === l.id
    )[0];
    if (location) {
      return location.tax_id_number;
    }
  }

  return null;
};

export const getPhysicalAddress = (salesOrder, _repairOrder) => {
  if (salesOrder) {
    const shipToAddress = get(salesOrder, "ship_to_address");
    const shipToCustomer = get(salesOrder, SHIP_TO_TYPE) === CUSTOMER;
    return merge(
      {
        company_id: shipToCustomer
          ? salesOrder.company_id
          : (salesOrder, "ship_to_company.id"),
        company_name: shipToCustomer
          ? salesOrder.company_name
          : get(salesOrder, "ship_to_company.name")
      },
      pojo(shipToAddress || {}).pick(
        COUNTRY,
        LINE1,
        LINE2,
        CITY,
        STATE,
        POSTAL_CODE
      )
    );
  }

  return {};
};

export const getUltimateAddress = (salesOrder, repairOrder) => {
  if (salesOrder && salesOrder.line_items) {
    return get(
      salesOrder,
      "final_destination_address",
      pojo(salesOrder || {}).pick(
        COUNTRY,
        COMPANY_NAME,
        LINE1,
        LINE2,
        CITY,
        STATE,
        POSTAL_CODE
      )
    );
  } else if (repairOrder && repairOrder.line_items) {
    return merge(get(repairOrder, "repair_station_physical_address", {}), {
      company_name: get(repairOrder, "company.name")
    });
  }

  return null;
};

export const getCustomerPO = (shippingRecord, salesOrder, repairOrder) =>
  (shippingRecord && shippingRecord[CUSTOMER_PURCHASE_ORDER_NUMBER]) ||
  (salesOrder && salesOrder[CUSTOMER_PURCHASE_ORDER_NUMBER]) ||
  (repairOrder && repairOrder[CODE]);

const parseSavedLineItem = (salesOrder, item) => {
  const savedLineItem =
    get(salesOrder, `commercial_invoice.${LINE_ITEMS}`, []).filter(
      l =>
        Number(l.part_id) === Number(item.part.id) &&
        l.serial_number === item.serial_number
    )[0] || {};

  if (savedLineItem.weight) savedLineItem.weight = Number(savedLineItem.weight);

  return savedLineItem;
};

export const getPrice = (salesOrder, repairOrder, lineItem) => {
  if (salesOrder && salesOrder.id) {
    return lineItem && lineItem.exchange_sale
      ? lineItem.exchange_fee
      : lineItem && lineItem.sale_price;
  } else if (repairOrder && repairOrder.id) {
    return lineItem && lineItem.subtotal;
  }

  return null;
};

export const setInvoiceInitialValues = ({
  lineItems,
  shippingRecord,
  salesOrder,
  repairOrder
}) => {
  let record;

  if (salesOrder && salesOrder.id) {
    record = salesOrder;
  } else if (repairOrder && repairOrder.id) {
    record = repairOrder;
  }

  return merge(
    {
      sales_order_id: get(salesOrder, "id"),
      repair_order_id: get(repairOrder, "id"),
      cargo_screening_consent: true,
      customer_po: getCustomerPO(shippingRecord, salesOrder, repairOrder),
      consignee:
        get(shippingRecord, TYPE) === RO_ITEM ? REPAIR_AND_RETURN : null,
      discreet: get(record, DISCREET) || false,
      ein: getEin(salesOrder, repairOrder),
      hazmat: get(record, LINE_ITEMS, [])
        .filter(l => lineItems.map(i => i.id).includes(l.inventory_id))
        .some(l => l.hazmat),
      weight: get(shippingRecord, WEIGHT),
      weight_unit: "lb",
      freight_forwarder_address: getPhysicalAddress(salesOrder, repairOrder),
      ultimate_consignee_address: getUltimateAddress(salesOrder, repairOrder),
      ultimate_consignee_name: get(record, "final_destination_name"),
      ultimate_consignee_email: get(record, "final_destination_email"),
      ultimate_consignee_phone: get(record, "final_destination_phone"),
      ultimate_consignee_reference: get(record, "final_destination_reference"),
      line_items:
        lineItems &&
        lineItems.map(item => {
          const recordLine = get(record, LINE_ITEMS, []).filter(
            l => l.inventory_id === item.id
          )[0];

          const savedLineItem = parseSavedLineItem(salesOrder, item);

          const lineItem = {
            part_id: item.part.id,
            serial_number: item.serial_number,
            condition: item.condition,
            weight: item.part.net_weight,
            weight_unit: item.part.net_weight_unit || "lb",
            weight_oz: item.part.net_weight_oz,
            ...pojo(item.part).pick(
              COUNTRY_OF_MFG,
              DESCRIPTION,
              HTSUS_CODE,
              PART_NUMBER,
              SCHEDULE_B_OR_HT_CODE,
              ECCN
            ),
            ...savedLineItem,
            quantity: Number(item.quantity) + Number(item.expected_quantity),
            price: getPrice(salesOrder, repairOrder, recordLine)
          };

          if (shippingRecord && salesOrder) {
            lineItem.sales_order_line_item_id = recordLine && recordLine.id;
            lineItem.inventory_id = item.id;
          } else if (shippingRecord && repairOrder) {
            lineItem.repair_order_line_item_id = recordLine && recordLine.id;
          } else {
            lineItem.inventory_id = item.id;
          }

          if (
            lineItem[ECCN] &&
            ["7A994", "9A991", "9A991"].includes(lineItem[ECCN])
          ) {
            lineItem[SCHEDULE_B_OR_HT_CODE] = "8803.30.0030";
          }

          return lineItem;
        })
    },
    removeKey(salesOrder.commercial_invoice, LINE_ITEMS) || {}
  );
};

export const SHIPPING_CHECKLIST = "Shipping Details";
export const SEND_WEIGHTS_AND_DIMS = "Send Weight and Dims";
export const COMMERCIAL_INVOICE = "Commercial Invoice";
export const COMMERCIAL_INVOICE_LINE_ITEMS = "CommercialInvoiceLineItems";
export const SAVE_OR_REPLACE_FORM = "SaveOrReplaceForm";
export const DOWNLOAD_PACKING_SLIP = "Download Packing Slip";

export const showFormFunc = ({
  salesOrder,
  repairOrder,
  setCurrentForm
}) => () => {
  if (
    salesOrder.line_items &&
    salesOrder.line_items &&
    salesOrder.line_items.length > 1
  ) {
    setCurrentForm(COMMERCIAL_INVOICE_LINE_ITEMS);
  } else if (
    repairOrder &&
    repairOrder.line_items &&
    repairOrder.line_items.length > 1
  ) {
    setCurrentForm(COMMERCIAL_INVOICE_LINE_ITEMS);
  } else {
    setCurrentForm(COMMERCIAL_INVOICE);
  }
};

export const addLineItemsFunc = ({
  setLineItems,
  setCurrentForm
}) => selectedLineItems => {
  setLineItems(selectedLineItems.map(l => l.inventory));
  setCurrentForm(COMMERCIAL_INVOICE);
};



// WEBPACK FOOTER //
// ./app/modules/shared/components/CommercialInvoice/helpers.js